import "./Onboarding.scss";

import { setUser } from "@sentry/react";
import { useAtom } from "jotai";
import { FC } from "react";
import { Navigate, Outlet } from "react-router-dom";

import { Background } from "@/components/Layout/Background/Background";
import { Footer } from "@/components/Layout/Footer/Footer";
import { DesktopHeading } from "@/components/Onboarding/DesktopHeading";
import { onboardingSections } from "@/components/Onboarding/Onboarding.constants";
import { OnboardingHeader } from "@/components/Onboarding/OnboardingHeader/OnboardingHeader";
import { OnboardingSidemenu } from "@/components/Onboarding/OnboardingSidemenu/OnboardingSidemenu";
import { StepGuard } from "@/components/Onboarding/StepGuard/StepGuard";
import { Wizard } from "@/components/Onboarding/Wizard/Wizard";
import { useGetCustomer } from "@/services/api";
import { isAnonymousUserAtom } from "@/utils/atoms";
import {
  clearEsimJourneyRedirectTarget,
  getEsimJourneyRedirectTarget,
} from "@/utils/esimJourneyRedirect";

/**
 * Root route for displaying the steps of the onboarding process.
 */
export const Onboarding: FC = () => {
  const [isAnonymousUser] = useAtom(isAnonymousUserAtom);

  const { data } = useGetCustomer(undefined, {
    query: {
      // If we come back to the onboarding, we want to refetch the data
      refetchOnMount: "always",
      onSuccess: (data) => {
        setUser({ id: data.id?.toString() });
      },
      enabled: !isAnonymousUser,
    },
  });

  const pendingEsimJourneyRedirectTarget = getEsimJourneyRedirectTarget();
  if (data?.onboardingFinished && pendingEsimJourneyRedirectTarget) {
    clearEsimJourneyRedirectTarget();

    return <Navigate to={pendingEsimJourneyRedirectTarget} replace={true} />;
  }

  if (data?.onboardingFinished) {
    return <Navigate to="/portal/cockpit" replace={true} />;
  }

  return (
    <Background>
      <OnboardingHeader />
      <div className="flex-grow bg-white">
        <StepGuard basePath="/onboarding">
          <main id="onboarding-wrapper">
            <OnboardingSidemenu />
            <DesktopHeading />
            <div className="onboarding-content">
              <Wizard basePath="/onboarding" sections={onboardingSections}>
                <Outlet context={"/onboarding"} />
              </Wizard>
            </div>
          </main>
        </StepGuard>
      </div>
      <Footer className="has-wave" />
    </Background>
  );
};
